import { useEffect } from 'react'
import { usePlaybackStore } from '../store'

interface UseKeyboardShortcutsOptions {
  onTogglePlay: () => void
  onSeekRelative: (delta: number) => void
  onNextVideo: () => void
  onPreviousVideo: () => void
  onToggleTheaterMode: () => void
}

export function useKeyboardShortcuts({
  onTogglePlay,
  onSeekRelative,
  onNextVideo,
  onPreviousVideo,
  onToggleTheaterMode,
}: UseKeyboardShortcutsOptions) {
  const {
    setVolume,
    setMuted,
    volume,
    muted,
    setFullscreen,
    isFullscreen,
    setSidebarOpen,
    isSidebarOpen,
    isTheaterMode,
    setTheaterMode,
  } = usePlaybackStore()

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Don't intercept when typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return
      }

      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault()
          onTogglePlay()
          break

        case 'ArrowLeft':
        case 'j':
          e.preventDefault()
          onSeekRelative(e.shiftKey ? -30 : -10)
          break

        case 'ArrowRight':
        case 'l':
          e.preventDefault()
          onSeekRelative(e.shiftKey ? 30 : 10)
          break

        case 'ArrowUp':
          e.preventDefault()
          setVolume(Math.min(1, volume + 0.1))
          break

        case 'ArrowDown':
          e.preventDefault()
          setVolume(Math.max(0, volume - 0.1))
          break

        case 'm':
          setMuted(!muted)
          break

        case 'n':
          e.preventDefault()
          onNextVideo()
          break

        case 'p':
          e.preventDefault()
          onPreviousVideo()
          break

        case 'f':
          e.preventDefault()
          setFullscreen(!isFullscreen)
          break

        case 't':
          e.preventDefault()
          onToggleTheaterMode()
          break

        case 's':
          e.preventDefault()
          setSidebarOpen(!isSidebarOpen)
          break

        case 'Escape':
          if (isTheaterMode) {
            setTheaterMode(false)
            break
          }
          if (isFullscreen) setFullscreen(false)
          break
      }
    }

    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [
    onTogglePlay,
    onSeekRelative,
    onNextVideo,
    onPreviousVideo,
    onToggleTheaterMode,
    volume,
    muted,
    isFullscreen,
    isSidebarOpen,
    isTheaterMode,
    setVolume,
    setMuted,
    setFullscreen,
    setSidebarOpen,
    setTheaterMode,
  ])
}
